import {
  EditIcon,
  PauseIcon,
  CardRemoveIcon,
  MessageIcon,
  CheckOutlineIcon,
  RequestModificationIcon,
} from '@/components/Icons'
import { MoreHorizontal, PlayCircleIcon, XCircle } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '../ui/button'
import { useState } from 'react'
import { showSuccessToast } from '../ui/toast'
import { UserActionDialog } from '@/components/UserActionDialog'
import { useNavigate } from 'react-router-dom'
import { PATHROUTES } from '@/constants/path'
import {
  AdvertisementActions,
  type AdvertisementActionsType,
} from '@/constants/sponsor'
import { useMutation } from '@tanstack/react-query'
import { queryClient } from '@/main'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { handleAxiosError } from '@/utils/helper'
import {
  updateAdvertisementAcknowledgement,
  updateAdvertisementStatus,
} from '@/api/sponsorApi'
import EditAdPlacementDrawer from './EditAdPlacementDrawer'
import RequestModificationDrawer from './RequestModificationDrawer'
import { AdvertisementStatus } from '@/schemas/sponsor.schems'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { useUserRole } from '@/contexts/UserRoleContext'

interface AdActionsDropdownProps {
  adId: string
  adName: string
  status: string
  sponsorShipId: string
  sponsorId: string
}

function AdActionsDropdown({
  adId,
  adName,
  status,
  sponsorShipId,
  sponsorId,
}: Readonly<AdActionsDropdownProps>) {
  const navigate = useNavigate()
  const [editAdplacementDrawerOpen, setEditAdplacementDrawerOpen] =
    useState(false)
  const [userActionDialogOpen, setUserActionDialogOpen] = useState(false)
  const [userActionType, setUserActionType] =
    useState<AdvertisementActionsType | null>(null)
  const [requestModificationDrawerOpen, setRequestModificationDrawerOpen] =
    useState(false)
  const { currentUser } = useUserRole()
  const isMessageAdmin = currentUser?.isChatAdmin

  const handleSetAction = (action: AdvertisementActionsType) => {
    setUserActionType(action)
    setUserActionDialogOpen(true)
  }

  const handleMessageAdminClick = () => {
    navigate(PATHROUTES.DASHBOARD.MESSAGE_ADMIN_WITH_ID(sponsorId), {
      state: {
        breadcrumbText: 'Sponsor Ad Management',
      },
    })
  }

  const getMessageSponsorButton = () => {
    return (
      <>
        {isMessageAdmin ? (
          <DropdownMenuItem
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-600 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
            onClick={handleMessageAdminClick}
          >
            <MessageIcon className="h-4 w-4" />
            Message Sponsor
          </DropdownMenuItem>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-300 cursor-not-allowed">
                <MessageIcon className="h-4 w-4" />
                Message Sponsor
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>Don't have access to message sponsor</p>
            </TooltipContent>
          </Tooltip>
        )}
      </>
    )
  }

  const renderMenuItems = () => {
    switch (status) {
      case AdvertisementStatus.DRAFT:
        return (
          <>
            <DropdownMenuItem
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-positive-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => handleSetAction(AdvertisementActions.approve)}
            >
              <CheckOutlineIcon className="h-4 w-4 text-ss-positive-200 hover:text-ss-positive-200" />
              <span className="text-ss-positive-200 hover:text-ss-positive-200">
                Approve Ad
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-critical-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => handleSetAction(AdvertisementActions.reject)}
            >
              <XCircle className="h-4 w-4 text-ss-critical-200 hover:text-ss-critical-200" />
              <span className="text-ss-critical-200 hover:text-ss-critical-200">
                Reject Ad
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-600 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => setRequestModificationDrawerOpen(true)}
            >
              <RequestModificationIcon className="h-4 w-4" />
              Request Modification
            </DropdownMenuItem>
          </>
        )

      case AdvertisementStatus.INACTIVE:
        return (
          <>
            <DropdownMenuItem className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-600 hover:bg-ss-action-gray-50 rounded-md cursor-pointer">
              <EditIcon className="h-4 w-4" />
              Edit Ad Placement
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-positive-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => handleSetAction(AdvertisementActions.active)}
            >
              <PlayCircleIcon className="h-4 w-4 text-ss-positive-200" />
              <span className="text-ss-positive-200">Resume Ad</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-critical-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => handleSetAction(AdvertisementActions.delete)}
            >
              <CardRemoveIcon className="h-4 w-4 text-ss-critical-200" />
              <span className="text-ss-critical-200">Remove Ad</span>
            </DropdownMenuItem>
          </>
        )

      case AdvertisementStatus.ACTIVE:
        return (
          <>
            <DropdownMenuItem
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-600 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => setEditAdplacementDrawerOpen(true)}
            >
              <EditIcon className="h-4 w-4" />
              Edit Ad Placement
            </DropdownMenuItem>
            {getMessageSponsorButton()}
            <DropdownMenuItem
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-critical-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => handleSetAction(AdvertisementActions.inactive)}
            >
              <PauseIcon className="h-4 w-4" />
              Pause Ad
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-critical-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => handleSetAction(AdvertisementActions.delete)}
            >
              <CardRemoveIcon className="h-4 w-4" />
              Remove Ad
            </DropdownMenuItem>
          </>
        )

      case AdvertisementStatus.REJECTED:
        return (
          <>
            {getMessageSponsorButton()}
            <DropdownMenuItem
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-critical-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
              onClick={() => handleSetAction(AdvertisementActions.delete)}
            >
              <CardRemoveIcon className="h-4 w-4" />
              Remove Ad
            </DropdownMenuItem>
          </>
        )

      default:
        return null
    }
  }

  let dialogTitle = ''
  let dialogSubtext = ''
  let dialogButton = ''
  let dialogDanger = false
  if (userActionType === AdvertisementActions.approve) {
    dialogTitle = 'Are you sure you want to approve this ad?'
    dialogSubtext =
      'Once approved, it will be visible on the website as per the scheduled placement.'
    dialogButton = 'Approve Ad'
  } else if (userActionType === AdvertisementActions.reject) {
    dialogTitle = 'Are you sure you want to reject this ad?'
    dialogSubtext =
      'Please provide a reason so the sponsor can make necessary changes.'
    dialogButton = 'Reject Ad'
    dialogDanger = true
  } else if (userActionType === AdvertisementActions.delete) {
    dialogTitle = 'Are you sure you want to permanently remove this ad?'
    dialogSubtext =
      'This action cannot be undone, and the sponsor will be notified.'
    dialogButton = 'Remove Ad'
    dialogDanger = true
  } else if (userActionType === AdvertisementActions.active) {
    dialogTitle = 'Are you sure you want to resume this Ad?'
    dialogSubtext =
      'Resuming will immediately make this ad live again on the event website. Performance tracking will continue from where it left off.'
    dialogButton = 'Resume Ad'
  } else if (userActionType === AdvertisementActions.inactive) {
    dialogTitle = 'Are you sure you want to pause this Ad?'
    dialogSubtext =
      'Pausing will immediately stop this ad from displaying on the event website. You can resume it anytime from your dashboard.'
    dialogButton = 'Pause Ad'
    dialogDanger = true
  }

  const {
    mutate: updateAdvertisementStatusMutation,
    isPending: isUpdatingAdvertisementStatus,
  } = useMutation({
    mutationFn: updateAdvertisementStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementList],
      })
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementById(adId)],
      })

      setUserActionDialogOpen(false)
      let toastTitle = ''
      let toastDesc = ''
      if (userActionType === AdvertisementActions.active) {
        toastTitle = 'Ad Restarted'
        toastDesc =
          'Your ad is now live again on the event website. You can monitor its performance in your dashboard.'
      } else if (userActionType === AdvertisementActions.inactive) {
        toastTitle = 'Ad Paused'
        toastDesc =
          'This ad has been successfully paused and is no longer visible on the event website. You can resume it anytime from your dashboard.'
      } else if (userActionType === AdvertisementActions.delete) {
        toastTitle = 'The ad has been removed successfully.'
        toastDesc = 'The sponsor has been notified.'
      }
      showSuccessToast(toastTitle, toastDesc)
      setUserActionType(null)
    },
    onError: error => {
      handleAxiosError('Failed to update advertisement status', error)
    },
  })

  const {
    mutate: updateAdvertisementACKMutation,
    isPending: isUpdatingAdvertisementACK,
  } = useMutation({
    mutationFn: updateAdvertisementAcknowledgement,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementList],
      })
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementById(adId)],
      })

      setUserActionDialogOpen(false)
      let toastTitle = ''
      let toastDesc = ''
      if (userActionType === AdvertisementActions.approve) {
        toastTitle = 'The ad has been approved successfully.'
        toastDesc = ''
      } else if (userActionType === AdvertisementActions.reject) {
        toastTitle = 'The ad has been rejected successfully.'
        toastDesc = 'The sponsor has been notified with your provided reason.'
      }
      showSuccessToast(toastTitle, toastDesc)
      setUserActionType(null)
    },
    onError: error => {
      handleAxiosError('Failed to update advertisement acknowledgement', error)
    },
  })

  const handleActionClick = async (
    action: AdvertisementActionsType,
    reason?: string
  ) => {
    const payload = {
      status: action,
      ...(reason && { reason }),
    }
    if (
      action === AdvertisementActions.approve ||
      action === AdvertisementActions.reject
    ) {
      updateAdvertisementACKMutation({
        id: adId,
        payload,
      })
    } else {
      updateAdvertisementStatusMutation({
        id: adId,
        payload,
      })
    }
  }

  return (
    <>
      {renderMenuItems() && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 w-8 p-0 hover:bg-ss-action-gray-50"
              aria-label={`Open actions for ${adName}`}
            >
              <MoreHorizontal className="h-5 w-5 text-ss-neutral-600" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-fit">
            {renderMenuItems()}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <EditAdPlacementDrawer
        open={editAdplacementDrawerOpen}
        onOpenChange={setEditAdplacementDrawerOpen}
        adId={adId}
        sponsorShipId={sponsorShipId}
      />
      <UserActionDialog
        open={userActionDialogOpen}
        onOpenChange={setUserActionDialogOpen}
        showForm={false}
        rejectAdReason={userActionType === AdvertisementActions.reject}
        danger={dialogDanger}
        title={dialogTitle}
        subtext={dialogSubtext}
        confirmButtonText={dialogButton}
        isLoading={isUpdatingAdvertisementStatus || isUpdatingAdvertisementACK}
        onConfirm={formData => {
          if (userActionType) {
            const reason =
              userActionType === AdvertisementActions.reject && formData.reason
                ? formData.reason
                : undefined
            handleActionClick(userActionType, reason)
          }
        }}
      />
      <RequestModificationDrawer
        open={requestModificationDrawerOpen}
        onOpenChange={setRequestModificationDrawerOpen}
        advertisementId={adId}
      />
    </>
  )
}

export default AdActionsDropdown
